import React, { useState, useEffect } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { apiService, Campaign, CampaignType } from "../../services";
import { authService } from "../../services/auth";

interface CampaignFormData {
  name: string;
  description: string;
  startDate: string;
  endDate: string;
  product: string;
  productQuantity: number;
  status: "active" | "inactive";
  productDescription: string;
  giftName?: string;
  webLink?: string;
  rateCode: number;
  winLimit?: number;
  type: CampaignType;
}

interface CampaignEditPageProps {
  onPageChange: (page: string) => void;
}

const CampaignEditPage: React.FC<CampaignEditPageProps> = ({
  onPageChange,
}) => {
  const { id: campaignId } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const isViewMode = searchParams.get("mode") === "view";
  const [campaign, setCampaign] = useState<Campaign | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string>("");

  const [formData, setFormData] = useState<CampaignFormData>({
    name: "",
    description: "",
    startDate: "",
    endDate: "",
    product: "",
    productQuantity: 0,
    status: "active",
    productDescription: "",
    giftName: "",
    webLink: "", // Initialize webLink
    rateCode: 1, // Initialize rateCode
    winLimit: 0, // Initialize winLimit as 0
    type: "normal",
  });

  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [showApiKey, setShowApiKey] = useState<boolean>(false);
  const [copiedApiKey, setCopiedApiKey] = useState<boolean>(false);
  const [exportError, setExportError] = useState<string>("");
  const [isExporting, setIsExporting] = useState<boolean>(false);

  const getMaskedApiKey = (key: string): string => {
    if (!key) return "Chưa có API key";
    return "••••••••••••••••";
  };

  const handleCopyApiKey = async () => {
    if (!campaign?.api_key) return;
    try {
      await navigator.clipboard.writeText(campaign.api_key);
      setCopiedApiKey(true);
      setTimeout(() => setCopiedApiKey(false), 1500);
    } catch (_err) {
      try {
        const el = document.createElement("textarea");
        el.value = campaign.api_key;
        document.body.appendChild(el);
        el.select();
        document.execCommand("copy");
        document.body.removeChild(el);
        setCopiedApiKey(true);
        setTimeout(() => setCopiedApiKey(false), 1500);
      } catch (_) {
        // Ignore copy errors silently
      }
    }
  };

  // Fetch campaign data
  useEffect(() => {
    const fetchCampaign = async () => {
      if (!campaignId) return;

      try {
        setIsLoading(true);
        const response = await apiService.getCampaign(campaignId);
        // Handle both API response formats: direct object or {success, data}
        const campaignData = (response as any).data || response;
        setCampaign(campaignData);
      } catch (error: any) {
        console.error("Failed to fetch campaign:", error);
        setError(error.message || "Không thể tải thông tin chiến dịch");
      } finally {
        setIsLoading(false);
      }
    };

    fetchCampaign();
  }, [campaignId]);

  // Xác định trạng thái chiến dịch dựa trên campaign ban đầu
  const now = new Date();
  const startDate = new Date(campaign?.start_date || "");
  const endDate = new Date(campaign?.end_date || "");

  // So sánh chỉ theo ngày, không theo giờ
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const startDay = new Date(
    startDate.getFullYear(),
    startDate.getMonth(),
    startDate.getDate()
  );
  const endDay = new Date(
    endDate.getFullYear(),
    endDate.getMonth(),
    endDate.getDate()
  );

  const isNotStarted = startDay > today;
  const isExpired = endDay < today;
  const isActive = !isNotStarted && !isExpired;

  // Kiểm tra trạng thái hết quà
  const usedGifts = campaign?.num_win_prizes || 0;
  const totalGifts = campaign?.total_gift || 0;
  const isOutOfGifts = totalGifts > 0 && usedGifts >= totalGifts;

  // Thêm logic tính toán trạng thái chiến dịch dựa trên formData (để cập nhật real-time)
  const getCurrentCampaignStatus = () => {
    if (!formData.startDate || !formData.endDate) {
      return { isNotStarted, isExpired, isActive, isOutOfGifts };
    }

    const currentStartDate = new Date(formData.startDate);
    const currentEndDate = new Date(formData.endDate);

    const currentStartDay = new Date(
      currentStartDate.getFullYear(),
      currentStartDate.getMonth(),
      currentStartDate.getDate()
    );
    const currentEndDay = new Date(
      currentEndDate.getFullYear(),
      currentEndDate.getMonth(),
      currentEndDate.getDate()
    );

    const currentIsNotStarted = currentStartDay > today;
    const currentIsExpired = currentEndDay < today;
    const currentIsActive = !currentIsNotStarted && !currentIsExpired;

    // Kiểm tra trạng thái hết quà dựa trên formData
    const currentUsedGifts = campaign?.num_win_prizes || 0;
    const currentTotalGifts = formData.productQuantity || 0;
    const currentIsOutOfGifts =
      currentTotalGifts > 0 && currentUsedGifts >= currentTotalGifts;

    return {
      isNotStarted: currentIsNotStarted,
      isExpired: currentIsExpired,
      isActive: currentIsActive,
      isOutOfGifts: currentIsOutOfGifts,
    };
  };

  const currentStatus = getCurrentCampaignStatus();

  // Get user role
  const isAdmin = authService.isAdmin();

  // Logic mới: Cho phép chỉnh sửa tất cả chiến dịch (bao gồm đã kết thúc)
  // User role: User chỉ được xem, Admin có thể chỉnh sửa tất cả
  const isViewOnlyMode = !isAdmin || isViewMode; // User role hoặc view mode từ query param - không cho chỉnh sửa
  const isFullEditMode = isAdmin && currentStatus.isNotStarted && !isViewMode; // Admin, chưa bắt đầu và không phải view mode - chỉnh sửa đầy đủ
  const isLimitedEditMode =
    isAdmin &&
    (currentStatus.isActive || currentStatus.isExpired) &&
    !isViewMode; // Admin, còn ngày hoặc đã kết thúc và không phải view mode - chỉnh sửa hạn chế

  // Khởi tạo form data từ campaign
  useEffect(() => {
    if (campaign) {
      // Format dates for datetime-local input
      const formatDateForInput = (dateString: string) => {
        if (!dateString) return "";
        const date = new Date(dateString);
        // Convert to YYYY-MM-DDTHH:MM format for datetime-local input
        return date.toISOString().slice(0, 16);
      };

      setFormData({
        name: campaign.name || "",
        description: campaign.description || "",
        startDate: formatDateForInput(campaign.start_date),
        endDate: formatDateForInput(campaign.end_date),
        product: campaign.product_name || "",
        productQuantity: campaign.total_gift || 0,
        status: campaign.status === 1 ? "active" : "inactive",
        productDescription: "",
        giftName: campaign.gift_name || "",
        webLink: campaign.landing_page_link || "",
        rateCode: campaign.rate_code || 1,
        winLimit: campaign.win_limit || 0,
        type: (campaign.type || "normal") as CampaignType,
      });

      // Không cần tính toán tổng số mã code nữa
    }
  }, [campaign]);

  // Không cần tính toán tổng số mã code nữa

  const handleInputChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >
  ) => {
    if (isViewOnlyMode) return; // Không cho phép chỉnh sửa trong chế độ xem

    const { name, value, type } = e.target;

    // Clear error for this field when user starts typing
    if (errors[name]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }

    if (type === "number") {
      const numValue = parseFloat(value) || 0;
      setFormData((prev) => ({
        ...prev,
        [name]: numValue,
      }));

      // Real-time validation cho số lượng quà (trừ trường hợp 0 = vô cực)
      if (name === "productQuantity") {
        const usedGifts = campaign?.num_win_prizes || 0;
        if (numValue > 0 && numValue < usedGifts) {
          setErrors((prev) => ({
            ...prev,
            productQuantity: `Số lượng quà không thể nhỏ hơn số lượng đã trúng (${usedGifts})`,
          }));
        } else {
          // Clear error if valid (bao gồm cả trường hợp 0 = vô cực)
          setErrors((prev) => {
            const newErrors = { ...prev };
            delete newErrors.productQuantity;
            return newErrors;
          });
        }
      }
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: value,
      }));
    }

    // Real-time validation for endDate
    if (name === "endDate" && value && formData.startDate) {
      const startDate = new Date(formData.startDate);
      const endDate = new Date(value);
      if (endDate <= startDate) {
        setErrors((prev) => ({
          ...prev,
          endDate: "Ngày kết thúc phải sau ngày bắt đầu",
        }));
      }
    }

    // Real-time validation for startDate (clear endDate error if startDate changes)
    if (name === "startDate" && value && formData.endDate) {
      const startDate = new Date(value);
      const endDate = new Date(formData.endDate);
      if (endDate <= startDate) {
        setErrors((prev) => ({
          ...prev,
          endDate: "Ngày kết thúc phải sau ngày bắt đầu",
        }));
      } else {
        // Clear endDate error if dates are now valid
        setErrors((prev) => {
          const newErrors = { ...prev };
          delete newErrors.endDate;
          return newErrors;
        });
      }
    }
  };

  const validateForm = () => {
    const newErrors: { [key: string]: string } = {};

    // Validate required fields
    if (!formData.name) {
      newErrors.name = "Tên chiến dịch là bắt buộc";
    }
    if (!formData.product) {
      newErrors.product = "Tên sản phẩm là bắt buộc";
    }
    if (!formData.startDate) {
      newErrors.startDate = "Ngày bắt đầu là bắt buộc";
    }
    if (!formData.endDate) {
      newErrors.endDate = "Ngày kết thúc là bắt buộc";
    }
    // Số lượng quà không bắt buộc
    if (!formData.webLink) {
      newErrors.webLink = "Link trang web là bắt buộc";
    }
    if (formData.rateCode <= 0) {
      newErrors.rateCode = "Tỷ lệ code phải lớn hơn 0";
    }
    // Bỏ validation giới hạn trúng thưởng và so sánh với số lượng quà
    // if (formData.winLimit === undefined || formData.winLimit === null) {
    //   newErrors.winLimit = "Vui lòng nhập giới hạn trúng thưởng";
    // } else if (formData.winLimit < 0) {
    //   newErrors.winLimit = "Giới hạn trúng thưởng không được âm";
    // } else if (
    //   formData.winLimit > 0 &&
    //   formData.winLimit > formData.productQuantity
    // ) {
    //   newErrors.winLimit =
    //     "Giới hạn trúng thưởng không được vượt quá số lượng quà";
    // }

    // Validate dates
    if (formData.startDate && formData.endDate) {
      const startDate = new Date(formData.startDate);
      const endDate = new Date(formData.endDate);
      if (startDate >= endDate) {
        newErrors.endDate = "Ngày kết thúc phải sau ngày bắt đầu";
      }
    }

    // Validate số lượng quà - không được nhỏ hơn số lượng đã trúng (trừ trường hợp 0 = vô cực)
    const usedGifts = campaign?.num_win_prizes || 0;
    if (formData.productQuantity > 0 && formData.productQuantity < usedGifts) {
      newErrors.productQuantity = `Số lượng quà không thể nhỏ hơn số lượng đã trúng (${usedGifts})`;
    }

    // Validate số lượng quà trong chế độ hạn chế (chỉ cho phép tăng) - trừ trường hợp 0 = vô cực
    if (
      isLimitedEditMode &&
      campaign &&
      formData.productQuantity > 0 &&
      formData.productQuantity < campaign.total_gift
    ) {
      newErrors.productQuantity =
        "Trong chế độ chỉnh sửa hạn chế, chỉ có thể tăng số lượng quà!";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (isViewOnlyMode) {
      onPageChange("campaigns");
      return;
    }

    // Validate form
    if (!validateForm()) {
      return;
    }

    if (!campaign) {
      alert("Không tìm thấy chiến dịch!");
      return;
    }

    // Cập nhật chiến dịch
    const updateData = {
      name: formData.name,
      description: formData.description,
      product_name: formData.product,
      gift_name: formData.giftName,
      total_gift: formData.productQuantity,
      start_date: formData.startDate,
      end_date: formData.endDate,
      landing_page_link: formData.webLink || "",
      status: formData.status === "active" ? 1 : 2,
      rate_code: formData.rateCode,
      rate_gift: 1, // Default value since field is removed from UI
      win_limit: formData.winLimit || 0,
      type: formData.type,
    };

    // Lưu chiến dịch đã cập nhật
    try {
      await apiService.updateCampaign(campaignId!, updateData);
    } catch (error: any) {
      console.error("Failed to update campaign:", error);
      alert(error.message || "Không thể cập nhật chiến dịch");
      return;
    }

    // Chuyển trực tiếp về trang quản lý chiến dịch
    onPageChange("campaigns");
  };

  const handleCancel = () => {
    onPageChange("campaigns");
  };

  const handleExportNewExcel = async () => {
    if (!campaignId) return;

    try {
      setIsExporting(true);
      setExportError("");

      const { blob, filename } = await apiService.exportCampaignCodes(
        "new",
        campaignId
      );

      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error: any) {
      console.error("Export error:", error);
      setExportError(error.message || "Có lỗi xảy ra khi xuất file");
    } finally {
      setIsExporting(false);
    }
  };

  const handleExportAllExcel = async () => {
    if (!campaignId) return;

    try {
      setIsExporting(true);
      setExportError("");

      const { blob, filename } = await apiService.exportCampaignCodes(
        "all",
        campaignId
      );

      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error: any) {
      console.error("Export error:", error);
      setExportError(error.message || "Có lỗi xảy ra khi xuất file");
    } finally {
      setIsExporting(false);
    }
  };

  const getPageTitle = () => {
    if (!isAdmin) return "Xem Chi Tiết Chiến Dịch";
    if (isViewOnlyMode) return "Xem Chi Tiết Chiến Dịch";
    if (isLimitedEditMode) return "Chỉnh Sửa Hạn Chế Chiến Dịch";
    if (isFullEditMode) return "Chỉnh Sửa Chiến Dịch";
    return "Chỉnh Sửa Chiến Dịch";
  };

  const getPageDescription = () => {
    if (!isAdmin) return "Bạn chỉ có quyền xem thông tin chiến dịch";
    if (isViewOnlyMode)
      return "Chiến dịch đã kết thúc - chỉ có thể xem thông tin";
    if (isLimitedEditMode)
      return "Chiến dịch đang diễn ra - có thể chỉnh sửa ngày kết thúc, tăng số lượng quà và thay đổi trạng thái";
    if (isFullEditMode)
      return "Chiến dịch chưa bắt đầu - có thể chỉnh sửa đầy đủ thông tin";
    return "Chỉnh sửa thông tin chiến dịch";
  };

  // Thêm helper function để hiển thị số lượng quà với logic vô cực
  const getDisplayQuantity = () => {
    const usedGifts = campaign?.num_win_prizes || 0;
    const totalGifts = formData.productQuantity || 0;

    if (totalGifts === 0) {
      return `${usedGifts}/∞`;
    }
    return `${usedGifts}/${totalGifts.toLocaleString()}`;
  };

  // Thêm helper function để tính phần trăm progress bar
  const getProgressPercentage = () => {
    const usedGifts = campaign?.num_win_prizes || 0;
    const totalGifts = formData.productQuantity || 0;

    if (totalGifts === 0) {
      return 0; // Không hiển thị progress bar khi vô cực
    }
    return Math.min((usedGifts / totalGifts) * 100, 100);
  };

  // Show loading state
  if (isLoading) {
    return (
      <div className="w-full">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          <span className="ml-3 text-gray-600 dark:text-gray-300">
            Đang tải thông tin chiến dịch...
          </span>
        </div>
      </div>
    );
  }

  // Show error state
  if (error) {
    return (
      <div className="w-full">
        <div className="bg-red-100 border border-red-400 text-red-700 dark:bg-red-900/30 dark:border-red-800 dark:text-red-300 px-4 py-3 rounded mb-4">
          {error}
        </div>
        <button
          onClick={handleCancel}
          className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
        >
          Quay lại
        </button>
      </div>
    );
  }

  if (!campaign) {
    return (
      <div className="w-full">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
            Không tìm thấy chiến dịch
          </h1>
          <button
            onClick={handleCancel}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
          >
            Quay lại
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full">
      {/* Header */}
      <div className="mb-8 flex justify-between items-start">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
            {getPageTitle()}
          </h1>
          <p className="text-gray-600 dark:text-gray-300">
            {getPageDescription()}
          </p>
          <div className="mt-3">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Loại chiến dịch
            </label>
            <div className="flex gap-4">
              <label
                className={`inline-flex items-center gap-2 ${
                  isViewOnlyMode ? "cursor-not-allowed opacity-60" : "cursor-pointer"
                }`}
              >
                <input
                  type="radio"
                  name="type"
                  checked={formData.type === "normal"}
                  onChange={() =>
                    !isViewOnlyMode &&
                    setFormData((prev) => ({ ...prev, type: "normal" }))
                  }
                  disabled={isViewOnlyMode}
                  className="h-4 w-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                />
                <span className="text-sm text-gray-800 dark:text-gray-200">
                  Thường
                </span>
              </label>
              <label
                className={`inline-flex items-center gap-2 ${
                  isViewOnlyMode ? "cursor-not-allowed opacity-60" : "cursor-pointer"
                }`}
              >
                <input
                  type="radio"
                  name="type"
                  checked={formData.type === "voucher"}
                  onChange={() =>
                    !isViewOnlyMode &&
                    setFormData((prev) => ({ ...prev, type: "voucher" }))
                  }
                  disabled={isViewOnlyMode}
                  className="h-4 w-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                />
                <span className="text-sm text-gray-800 dark:text-gray-200">
                  Voucher
                </span>
              </label>
            </div>
          </div>
        </div>
      </div>

      {/* Warning Messages - Chiến dịch đang diễn ra */}
      {isLimitedEditMode &&
        currentStatus.isActive &&
        !currentStatus.isOutOfGifts && (
          <div className="mb-6 p-4 rounded-lg shadow-sm bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800">
            <div className="flex items-start">
              <div className="flex-shrink-0 mt-1">
                <svg
                  className="h-5 w-5 text-green-500"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M13 10V3L4 14h7v7l9-11h-7z"
                  />
                </svg>
              </div>
              <div className="ml-3 flex-1">
                <h3 className="text-sm font-medium text-green-800 dark:text-green-200 mb-2">
                  Chiến dịch đang diễn ra
                </h3>

                {/* Progress bar cho tổng số mã mới - cập nhật với logic vô cực */}
                <div className="text-lg font-bold text-green-800 dark:text-green-200 mb-2">
                  {getDisplayQuantity()}
                </div>
                <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-3 mb-2 overflow-hidden">
                  <div
                    className="bg-green-500 h-3 rounded-full transition-all duration-1000 ease-out animate-progress-fill"
                    style={{
                      width: `${getProgressPercentage()}%`,
                    }}
                  ></div>
                </div>
                <div className="text-sm text-green-700 dark:text-green-300 text-right">
                  Còn{" "}
                  {Math.ceil(
                    (new Date(formData.endDate).getTime() -
                      new Date().getTime()) /
                      (1000 * 60 * 60 * 24)
                  )}{" "}
                  ngày
                </div>
                <div className="text-xs text-green-600 dark:text-green-300 mt-2">
                  Thông Tin Có Thể Chỉnh Sửa: Số lượng quà cho phép nhập và
                  chỉnh sửa
                </div>
              </div>
            </div>
          </div>
        )}

      {/* Warning Messages - Chiến dịch hết quà */}
      {isLimitedEditMode &&
        currentStatus.isActive &&
        currentStatus.isOutOfGifts && (
          <div className="mb-6 p-4 rounded-lg shadow-sm bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800">
            <div className="flex items-start">
              <div className="flex-shrink-0 mt-1">
                <svg
                  className="h-5 w-5 text-blue-500"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"
                  />
                </svg>
              </div>
              <div className="ml-3 flex-1">
                <h3 className="text-sm font-medium text-blue-800 dark:text-blue-200 mb-2">
                  Chiến dịch hết quà
                </h3>

                {/* Progress bar cho tổng số mã mới - cập nhật với logic vô cực */}
                <div className="text-lg font-bold text-blue-800 dark:text-blue-200 mb-2">
                  {getDisplayQuantity()}
                </div>
                <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-3 mb-2 overflow-hidden">
                  <div
                    className="bg-blue-500 h-3 rounded-full transition-all duration-1000 ease-out animate-progress-fill"
                    style={{
                      width: `${getProgressPercentage()}%`,
                    }}
                  ></div>
                </div>
                <div className="text-sm text-blue-700 dark:text-blue-300 text-right">
                  Còn{" "}
                  {Math.ceil(
                    (new Date(formData.endDate).getTime() -
                      new Date().getTime()) /
                      (1000 * 60 * 60 * 24)
                  )}{" "}
                  ngày
                </div>
                <div className="text-xs text-blue-600 dark:text-blue-300 mt-2">
                  Thông Tin Có Thể Chỉnh Sửa: Tăng số lượng quà để tiếp tục
                  chiến dịch
                </div>
              </div>
            </div>
          </div>
        )}

      {/* Warning Messages - Chiến dịch đã kết thúc */}
      {isLimitedEditMode && currentStatus.isExpired && (
        <div className="mb-6 p-4 rounded-lg shadow-sm bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-800">
          <div className="flex items-start">
            <div className="flex-shrink-0 mt-1">
              <svg
                className="h-5 w-5 text-orange-500"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"
                />
              </svg>
            </div>
            <div className="ml-3 flex-1">
              <h3 className="text-sm font-medium text-orange-800 dark:text-orange-200 mb-2">
                Chiến dịch đã kết thúc
              </h3>

              {/* Progress bar cho tổng số mã mới - cập nhật với logic vô cực */}
              <div className="text-lg font-bold text-orange-800 dark:text-orange-200 mb-2">
                {getDisplayQuantity()}
              </div>
              <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-3 mb-2 overflow-hidden">
                <div
                  className="bg-orange-500 h-3 rounded-full transition-all duration-1000 ease-out animate-progress-fill"
                  style={{
                    width: `${getProgressPercentage()}%`,
                  }}
                ></div>
              </div>
              <div className="text-sm text-orange-700 dark:text-orange-300 text-right">
                Đã kết thúc
              </div>
              <div className="text-xs text-orange-600 dark:text-orange-300 mt-2">
                Thông Tin Có Thể Chỉnh Sửa: Số lượng quà và thông tin cơ bản
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Thông báo cho chế độ "Chưa bắt đầu" */}
      {isFullEditMode && (
        <div className="mb-6 p-4 rounded-lg shadow-sm bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800">
          <div className="flex items-start">
            <div className="flex-shrink-0 mt-1">
              <svg
                className="h-5 w-5 text-blue-500"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  fillRule="evenodd"
                  d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                  clipRule="evenodd"
                />
              </svg>
            </div>
            <div className="ml-3 flex-1">
              <h3 className="text-sm font-medium text-blue-800 dark:text-blue-200 mb-2">
                Chiến dịch chưa bắt đầu
              </h3>

              {/* Progress bar cho tổng số mã mới - cập nhật với logic vô cực */}
              <div className="text-lg font-bold text-green-800 dark:text-green-200 mb-2">
                {getDisplayQuantity()}
              </div>
              <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-3 mb-2">
                <div
                  className="bg-blue-800 h-3 rounded-full transition-all duration-300"
                  style={{
                    width: "0%",
                  }}
                ></div>
              </div>
              <div className="text-sm text-blue-800 dark:text-blue-200 text-right">
                Bắt đầu:{" "}
                {formData.startDate
                  ? new Date(formData.startDate).toLocaleDateString("vi-VN")
                  : "Chưa xác định"}
              </div>
            </div>
          </div>
        </div>
      )}

      {isViewOnlyMode && (
        <div className="mb-6 p-4 bg-gray-50 dark:bg-gray-800/50 border border-gray-200 dark:border-gray-700 rounded-lg">
          <div className="flex items-start">
            <div className="flex-shrink-0 mt-1">
              <svg
                className={`h-5 w-5 ${
                  isOutOfGifts ? "text-blue-500" : "text-gray-500"
                }`}
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path d="M10 12a2 2 0 100-4 2 2 0 000 4z" />
                <path
                  fillRule="evenodd"
                  d="M.458 10C1.732 5.943 5.522 3 10 3s8.268 2.943 9.542 7c-1.274 4.057-5.064 7-9.542 7S1.732 14.057.458 10zM14 10a4 4 0 11-8 0 4 4 0 018 0z"
                  clipRule="evenodd"
                />
              </svg>
            </div>
            <div className="ml-3 flex-1">
              <h3
                className={`text-sm font-medium mb-2 ${
                  isOutOfGifts
                    ? "text-blue-600 dark:text-blue-300"
                    : "text-gray-700 dark:text-gray-300"
                }`}
              >
                Chế độ xem chi tiết
              </h3>

              {/* Progress bar cho tổng số mã mới - cập nhật với logic vô cực */}
              <div className="text-lg font-bold text-green-800 dark:text-green-200 mb-2">
                {getDisplayQuantity()}
              </div>
              <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-3 mb-2 overflow-hidden">
                <div
                  className="bg-green-500 h-3 rounded-full transition-all duration-1000 ease-out animate-progress-fill"
                  style={{
                    width: `${getProgressPercentage()}%`,
                  }}
                ></div>
              </div>
              <div
                className={`text-sm text-right ${
                  isOutOfGifts
                    ? "text-blue-600 dark:text-blue-300"
                    : "text-gray-600 dark:text-gray-300"
                }`}
              >
                Chiến dịch đã kết thúc
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Form */}
      <form
        onSubmit={handleSubmit}
        className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 px-4 py-4 w-full"
      >
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Cột 1: Phần có thể chỉnh sửa */}
          <div className="space-y-6">
            <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-100 border-b border-gray-200 dark:border-gray-700 pb-2">
              {isLimitedEditMode
                ? "Thông Tin Có Thể Chỉnh Sửa"
                : isFullEditMode
                ? "Thông Tin Có Thể Chỉnh Sửa"
                : "Thông Tin Không Thể Chỉnh Sửa"}
            </h3>

            {/* Số lượng quà - tùy chọn (trong chế độ hạn chế chỉ cho phép tăng) */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Số Lượng Quà
              </label>
              <input
                type="number"
                name="productQuantity"
                value={formData.productQuantity}
                onChange={handleInputChange}
                // Không bắt buộc, không áp min khi không ở chế độ hạn chế
                min={isLimitedEditMode ? formData.productQuantity : undefined}
                onWheel={(e) => e.currentTarget.blur()}
                className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                  isViewOnlyMode
                    ? "bg-gray-100 dark:bg-gray-700/60 cursor-not-allowed"
                    : "bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                } ${
                  errors.productQuantity
                    ? "border-red-500"
                    : "border-gray-300 dark:border-gray-600"
                }`}
                placeholder="0"
                disabled={isViewOnlyMode}
              />
              {errors.productQuantity && (
                <p className="mt-1 text-sm text-red-600">
                  {errors.productQuantity}
                </p>
              )}
            </div>

            {/* Tỷ lệ code */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Tỷ Lệ Code <span className="text-red-500">*</span>
              </label>
              <input
                type="number"
                name="rateCode"
                value={formData.rateCode}
                onChange={handleInputChange}
                min="1"
                onWheel={(e) => e.currentTarget.blur()}
                className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                  isViewOnlyMode
                    ? "bg-gray-100 dark:bg-gray-700/60 cursor-not-allowed"
                    : "bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                } ${
                  errors.rateCode
                    ? "border-red-500"
                    : "border-gray-300 dark:border-gray-600"
                }`}
                placeholder="1"
                required
                disabled={isViewOnlyMode}
              />
              {errors.rateCode && (
                <p className="mt-1 text-sm text-red-600">{errors.rateCode}</p>
              )}
            </div>

            {/* Giới hạn trúng thưởng */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Giới Hạn Trúng Thưởng <span className="text-red-500">*</span>
              </label>
              <input
                type="number"
                name="winLimit"
                value={formData.winLimit || 0}
                onChange={handleInputChange}
                min="1"
                onWheel={(e) => e.currentTarget.blur()}
                className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                  isViewOnlyMode
                    ? "bg-gray-100 dark:bg-gray-700/60 cursor-not-allowed"
                    : "bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                } ${
                  errors.winLimit
                    ? "border-red-500"
                    : "border-gray-300 dark:border-gray-600"
                }`}
                placeholder="Nhập số lần trúng thưởng tối đa"
                required
                disabled={isViewOnlyMode}
              />
              <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                Số lần tối đa một số điện thoại có thể trúng thưởng
              </p>
              {errors.winLimit && (
                <p className="mt-1 text-sm text-red-600">{errors.winLimit}</p>
              )}
            </div>

            {/* Thời gian kết thúc - Chỉ cho phép chỉnh sửa trong chế độ hạn chế và đầy đủ */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Thời Gian Kết Thúc <span className="text-red-500"></span>
              </label>
              <input
                type="datetime-local"
                name="endDate"
                value={formData.endDate}
                onChange={handleInputChange}
                min={new Date().toISOString().slice(0, 16)}
                className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                  isViewOnlyMode
                    ? "bg-gray-100 dark:bg-gray-700/60 cursor-not-allowed"
                    : "bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                } ${
                  errors.endDate
                    ? "border-red-500"
                    : "border-gray-300 dark:border-gray-600"
                }`}
                required
                disabled={isViewOnlyMode}
              />
              {errors.endDate && (
                <p className="mt-1 text-sm text-red-600">{errors.endDate}</p>
              )}
            </div>

            {/* Mô tả chiến dịch */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Mô Tả
              </label>
              <textarea
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                rows={5}
                className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                  isViewOnlyMode
                    ? "bg-gray-100 dark:bg-gray-700/60 cursor-not-allowed"
                    : "bg-white dark:bg-gray-700 text-gray-900 dark:text-white border-gray-300 dark:border-gray-600"
                }`}
                placeholder="Nhập mô tả chiến dịch..."
                disabled={isViewOnlyMode}
              />
            </div>

            {/* Trạng thái chiến dịch - Chỉ cho phép chỉnh sửa trong chế độ đầy đủ */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Trạng thái
              </label>
              <div className="flex items-center justify-between px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700">
                <div className="flex flex-col">
                  <span className="text-sm text-gray-700 dark:text-gray-200">
                    {formData.status === "active" ? "Đang chạy" : "Đang dừng"}
                  </span>
                </div>
                <button
                  type="button"
                  onClick={() =>
                    setFormData((prev) => ({
                      ...prev,
                      status: prev.status === "active" ? "inactive" : "active",
                    }))
                  }
                  disabled={isViewOnlyMode}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                    formData.status === "active" ? "bg-blue-500" : "bg-gray-200"
                  } ${
                    isViewOnlyMode
                      ? "opacity-50 cursor-not-allowed"
                      : "cursor-pointer"
                  }`}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      formData.status === "active"
                        ? "translate-x-6"
                        : "translate-x-1"
                    }`}
                  />
                </button>
              </div>
            </div>
          </div>

          {/* Cột 2: Thông tin cơ bản */}
          <div className="space-y-6">
            <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-100 border-b border-gray-200 dark:border-gray-700 pb-2">
              Thông Tin Cơ Bản
            </h3>

            {/* Tên chiến dịch */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Tên Chiến Dịch
              </label>
              <div className="px-4 py-3 bg-gray-50 dark:bg-gray-900/40 border border-gray-200 dark:border-gray-700 rounded-lg text-gray-900 dark:text-gray-100 font-medium">
                {formData.name}
              </div>
            </div>

            {/* Link dẫn web */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Link Dẫn Web
              </label>
              <div className="px-4 py-3 bg-gray-50 dark:bg-gray-900/40 border border-gray-200 dark:border-gray-700 rounded-lg text-gray-900 dark:text-gray-100 font-medium break-all">
                {formData.webLink || "Chưa có link"}
              </div>
            </div>

            {/* Tên sản phẩm */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Tên Sản Phẩm
              </label>
              <div className="px-4 py-3 bg-gray-50 dark:bg-gray-900/40 border border-gray-200 dark:border-gray-700 rounded-lg text-gray-900 dark:text-gray-100 font-medium">
                {formData.product}
              </div>
            </div>

            {/* Tên quà */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Tên Quà
              </label>
              <div className="px-4 py-3 bg-gray-50 dark:bg-gray-900/40 border border-gray-200 dark:border-gray-700 rounded-lg text-gray-900 dark:text-gray-100 font-medium">
                {formData.giftName || "Chưa có tên quà"}
              </div>
            </div>

            {/* Thời gian bắt đầu */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Thời Gian Bắt Đầu
              </label>
              <div className="px-4 py-3 bg-gray-50 dark:bg-gray-900/40 border border-gray-200 dark:border-gray-700 rounded-lg text-gray-900 dark:text-gray-100 font-medium">
                {new Date(formData.startDate).toLocaleString("vi-VN")}
              </div>
            </div>
          </div>

          {/* Cột 3: Thông tin cấu hình và mã code */}
          <div className="space-y-6">
            <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-100 border-b border-gray-200 dark:border-gray-700 pb-2">
              Cấu Hình & Mã Code
            </h3>

            {/* API Key */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                API Key
              </label>
              <div className="px-3 py-2 bg-gray-50 dark:bg-gray-900/40 border border-gray-200 dark:border-gray-700 rounded-lg flex items-center justify-between gap-2">
                <span className="text-gray-900 dark:text-gray-100 font-medium font-mono whitespace-nowrap overflow-hidden text-ellipsis max-w-[70%]">
                  {showApiKey
                    ? campaign?.api_key || "Chưa có API key"
                    : getMaskedApiKey(campaign?.api_key || "")}
                </span>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <button
                    type="button"
                    onClick={() => setShowApiKey((v) => !v)}
                    className="p-1.5 rounded hover:bg-gray-100 dark:hover:bg-gray-700/60 text-blue-600 dark:text-blue-400"
                    title={showApiKey ? "Ẩn API Key" : "Hiện API Key"}
                  >
                    {showApiKey ? (
                      <svg
                        className="w-5 h-5"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                      >
                        <path
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M13.875 18.825A10.05 10.05 0 0112 19c-4.5 0-8.268-2.943-9.543-7a10.05 10.05 0 012.53-4.152M6.223 6.223A9.956 9.956 0 0112 5c4.5 0 8.268 2.943 9.543 7a10.05 10.05 0 01-3.307 4.6M3 3l18 18"
                        />
                      </svg>
                    ) : (
                      <svg
                        className="w-5 h-5"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                      >
                        <path
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M2.458 12C3.732 7.943 7.522 5 12 5s8.268 2.943 9.542 7c-1.274 4.057-5.064 7-9.542 7s-8.268-2.943-9.542-7z"
                        />
                        <circle cx="12" cy="12" r="3" strokeWidth="2" />
                      </svg>
                    )}
                  </button>
                  <button
                    type="button"
                    onClick={handleCopyApiKey}
                    className="p-1.5 rounded hover:bg-gray-100 dark:hover:bg-gray-700/60 text-gray-700 dark:text-gray-200"
                    title="Sao chép API Key"
                  >
                    <svg
                      className="w-5 h-5"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                    >
                      <rect
                        x="9"
                        y="9"
                        width="13"
                        height="13"
                        rx="2"
                        ry="2"
                        strokeWidth="2"
                      />
                      <path
                        d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1"
                        strokeWidth="2"
                        strokeLinecap="round"
                      />
                    </svg>
                  </button>
                </div>
              </div>
              {copiedApiKey && (
                <div className="text-xs text-green-600 mt-1">Đã sao chép</div>
              )}
            </div>

            {/* Thông tin mã code hiện tại */}
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex justify-end space-x-4 mt-10 pt-8 border-t border-gray-200 dark:border-gray-700">
          {isViewOnlyMode ? (
            <button
              type="button"
              onClick={handleCancel}
              className="px-4 py-3 bg-gray-600 text-white rounded-md hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:border-transparent transition-colors"
            >
              Hủy
            </button>
          ) : (
            <>
              <button
                type="button"
                onClick={handleCancel}
                className="px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-md text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:border-transparent transition-colors"
              >
                Hủy
              </button>
              <button
                type="submit"
                className="px-4 py-3 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors"
              >
                {isLimitedEditMode
                  ? "Cập Nhật"
                  : isFullEditMode
                  ? "Cập Nhật Đầy Đủ"
                  : "Cập Nhật"}
              </button>
            </>
          )}
        </div>
      </form>
    </div>
  );
};

export default CampaignEditPage;
